import { Button } from '@/src/common/components/Button'
import { View, ScrollView, Text, StyleSheet, Platform } from 'react-native'
import { BackButton } from '@/src/common/components/BackButton'
import { Routes } from '@/src/common/enums/routes'
import { type Address, AutoCompleteAddress } from '@/src/common/components/AutoCompleteAddress'
import { useCallback, useEffect, useState } from 'react'
import { useFocusEffect, useRouter } from 'expo-router'
import useAddAddressInformationStore from '@/src/common/store/useAddAddressInformationStore'
import { useMutation } from '@apollo/client'
import {
  UPDATE_USER_ADDRESS,
  VALIDATE_AND_UPDATE_HIGHNOTE_ADDRESS,
} from '@/src/common/gql/mutations/user'
import { useLogError } from '@/src/common/hooks/useLogError'
import { Switch } from '@/src/common/components/Switch'
import type { ValidateAndUpdateHighnoteAddressMutation } from '@api/mesa/graphql'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { useSardine } from '@/src/services/Sardine'
import { CardApplicationWrapper } from './cardApplicationWrapper'
import { mesaColorNames, typography } from '@/src/common/theme'
import { cardApplicationStyles as commonStyle } from './common'
import * as E2E from '../../e2e'

const ADDRESS_VALIDATION_ERROR =
  'The mailing address provided is not valid. Please make any corrections to proceed with your application.'

export function FindYourHome() {
  const Alert = useAlert(FindYourHome.name)
  const router = useRouter()
  const [primaryAddress, setPrimaryAddress] = useState<Address>()
  const [isPrimaryAddressValid, setIsPrimaryAddressValid] = useState(false)
  const [mailingAddress, setMailingAddress] = useState<Address>()
  const [isMailingAddressValid, setIsMailingAddressValid] = useState(false)
  const [isMailingAddressDifferent, setIsMailingAddressDifferent] = useState(false)
  const [apolloError, setApolloError] = useState<string>()
  const [isMutatingAddress, setIsMutatingAddress] = useState(false)
  const [shouldShowValidationErrors, setShouldShowValidationErrors] = useState(false)
  const [updateUserAddress] = useMutation(UPDATE_USER_ADDRESS)
  const [validateAndUpdateMailingAddress, { loading: isContinueButtonLoading }] = useMutation(
    VALIDATE_AND_UPDATE_HIGHNOTE_ADDRESS,
  )
  const logError = useLogError()
  const [isContinueEnabled, setIsContinueEnabled] = useState(false)
  const [addressValidationError, setAddressValidationError] = useState('')
  const { updateOptions } = useSardine()

  useEffect(validateAddressStates, [
    primaryAddress,
    mailingAddress,
    isMutatingAddress,
    isMailingAddressDifferent,
  ])

  useEffect(() => {
    void updateOptions({ flow: 'application-find-your-home', enableGeoLocation: true })
  }, [updateOptions])

  function validateAddressStates() {
    if (primaryAddress == null || isMutatingAddress) {
      setIsContinueEnabled(false)
      return
    }

    // if mailing address doesn't exist and mailing address is not different, enable continue button
    if (!mailingAddress && !isMailingAddressDifferent) {
      setMailingAddress(primaryAddress)
      setIsContinueEnabled(true)
    }

    // if user toggles mailing address is different, let's give them a blank address slate
    if (
      isMailingAddressDifferent &&
      JSON.stringify(primaryAddress) === JSON.stringify(mailingAddress)
    ) {
      setMailingAddress(undefined)
    }

    // if user toggles mailing address is not different, let's set mailing address back to primary address
    if (!isMailingAddressDifferent) {
      setMailingAddress(primaryAddress)
      setIsContinueEnabled(true)
    }

    // block continue button if mailing address is different but no mailing address entered
    if (isMailingAddressDifferent && mailingAddress == null) {
      setIsContinueEnabled(false)
      return
    }

    setIsContinueEnabled(true)
  }

  const { setUserPrimaryAddress, setUserMailingAddress, userPrimaryAddress, userMailingAddress } =
    useAddAddressInformationStore()

  function refreshAddressInformation() {
    setPrimaryAddress(userPrimaryAddress)
    setMailingAddress(userMailingAddress)
    setIsMutatingAddress(false)
  }

  useFocusEffect(useCallback(refreshAddressInformation, [userPrimaryAddress, userMailingAddress]))

  function getChangedMailingAddressBodyText(originalAddress: Address, changedAddress: Address) {
    return `Please review and proceed with the below mailing address or make any corrections to proceed.

Original Address:

${originalAddress.street1}${originalAddress.street2} ${originalAddress.city} ${originalAddress.state} ${originalAddress.zipCode}

Suggested Addresss:

${changedAddress.street1}${changedAddress.street2} ${changedAddress.city}

${changedAddress.state} ${changedAddress.zipCode}` as const
  }

  function handleAddressValidationResponse(
    validationData: ValidateAndUpdateHighnoteAddressMutation['validateHighnoteAddress'],
  ) {
    if (
      validationData.addressValidationOutcome === 'AddressIncompleteResult' ||
      validationData.addressValidationOutcome === 'AddressInvalidResult'
    ) {
      setAddressValidationError(ADDRESS_VALIDATION_ERROR)
      setIsContinueEnabled(true)
    } else if (validationData.addressValidationOutcome === 'AddressValidatedWithChangesResult') {
      if (mailingAddress == null) {
        return
      }

      const standardizedMailingAddress = {
        street1: validationData.standarizedStreetAddress?.trim() ?? '',
        street2: validationData.standarizedExtendedAddress?.trim() ?? '',
        city: validationData.standarizedLocality?.trim() ?? '',
        state: validationData.standarizedRegion?.trim() ?? '',
        zipCode: validationData.standarizedPostalCode?.trim() ?? '',
      } satisfies Address

      Alert.alert(
        'Address Cannot Be Verified',
        getChangedMailingAddressBodyText(mailingAddress, standardizedMailingAddress),
        [
          {
            text: 'Use Suggested Address',
            onPress: () => {
              if (!isMailingAddressDifferent) {
                setPrimaryAddress(standardizedMailingAddress)
              }
              setMailingAddress(standardizedMailingAddress)
              setIsContinueEnabled(true)
            },
            style: 'default',
          },
          {
            text: 'Change Original Address',
            onPress: () => {
              setIsContinueEnabled(true)
            },
            style: 'default',
          },
        ],
      )
    } else if (validationData.addressValidationOutcome === 'AddressValidatedResult') {
      setUserMailingAddress(mailingAddress)
      router.push(Routes.LastQuestion.route)
    } else {
      // at this point, we've now checked all actual Address Validation response types - if we're here, we have a user or access error and need to alert (https://highnote.com/docs/issuing/cards/physical-card#validate-shipping-address)
      setAddressValidationError('Unable to validate address - please try again later')
      throw new Error('Failed to perform Highnote Address Validation')
    }
  }

  async function savePrimaryAddress() {
    if (primaryAddress == null) {
      return
    }

    setIsMutatingAddress(true)
    const { street2, ...otherAddressFields } = primaryAddress
    const variables = street2 !== '' ? { ...otherAddressFields, street2 } : otherAddressFields

    await updateUserAddress({
      variables,
      onCompleted() {
        setUserPrimaryAddress(primaryAddress)
        setIsMutatingAddress(false)
      },
      onError(error) {
        setApolloError(
          `We weren't able to validate your primary address at this time, please try again`,
        )
        logError(error)
        setIsMutatingAddress(false)
      },
    })
  }

  async function saveMailingAddress() {
    if (mailingAddress == null) {
      return
    }

    await validateAndUpdateMailingAddress({
      variables: {
        streetAddress: mailingAddress.street1.trim(),
        extendedAddress: mailingAddress.street2.trim(),
        locality: mailingAddress.city.trim(),
        region: mailingAddress.state.trim(),
        zipCode: mailingAddress.zipCode.trim(),
      },
      onCompleted(data) {
        handleAddressValidationResponse(data.validateHighnoteAddress)
      },
      onError(error) {
        setApolloError(
          `We weren't able to validate your mailing address at this time, please try again`,
        )
        logError(error)
        setIsMutatingAddress(false)
      },
    })
  }

  const onContinuePress = () => {
    if (!isPrimaryAddressValid) {
      setShouldShowValidationErrors(true)
      return
    }

    if (isMailingAddressDifferent && !isMailingAddressValid) {
      setShouldShowValidationErrors(true)
      return
    }

    void savePrimaryAddress()
    void saveMailingAddress()
  }

  return (
    <CardApplicationWrapper paddingHorizontal={0}>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        testID={E2E.Config.Ids.View.FindYourHome}
        style={[{ paddingHorizontal: 16 }, Platform.OS === 'web' && { overflow: 'visible' }]}
      >
        <View style={{ flex: 1 }}>
          <BackButton
            href={Routes.OfferCode.route}
            text="Back"
            testID={E2E.Config.Ids.Press.FindHomeBack}
          />

          <View style={commonStyle.headerText}>
            <Text style={typography['heading/heading2']}>What's your primary address?</Text>
          </View>
          <Text style={[typography['paragraph/md'], commonStyle.instructionText]}>
            We'll ship your card to your mailing address.
          </Text>

          <View>
            <AutoCompleteAddress
              mode="card-application-address"
              onChange={(address, isValid) => {
                setPrimaryAddress(address)
                setIsPrimaryAddressValid(isValid)
              }}
              address={primaryAddress}
              showErrors={shouldShowValidationErrors}
              style={{ paddingTop: -888, paddingBottom: -888 }}
              testIDPrefix={E2E.Config.Ids.Input.PrimaryAddress.Prefix}
              sardineIds={{
                autoComplete: 'autocomplete',
                fullAddress: {
                  street1: 'street1',
                  street2: 'street2',
                  city: 'city',
                  state: 'state',
                  zipCode: 'zip',
                },
              }}
            />

            <Text style={[typography['paragraph/sm'], styles.addressRestrictionText]}>
              Currently, we are only available in the United States. PO Box, PMB, business,
              temporary, and non-US based addresses are not accepted.
            </Text>

            <Switch
              checked={isMailingAddressDifferent}
              onCheckedChange={setIsMailingAddressDifferent}
              testID={E2E.Config.Ids.Switch.MailingAddressDifferent}
              label={{
                render: ({ switchComponent }) => (
                  <>
                    {switchComponent}
                    <Text
                      style={[
                        typography['paragraph/sm'],
                        { color: mesaColorNames['Greyscale/400 #A4AAAC'] },
                      ]}
                    >
                      My mailing address is different
                    </Text>
                  </>
                ),
              }}
            />
          </View>
          <View style={{ marginVertical: 16 }}>
            {isMailingAddressDifferent && (
              <AutoCompleteAddress
                mode="card-physical-ship-address"
                onChange={(mailingAddress, isValid) => {
                  setMailingAddress(mailingAddress)
                  setIsMailingAddressValid(isValid)
                }}
                address={mailingAddress}
                showErrors={shouldShowValidationErrors}
                testIDPrefix={E2E.Config.Ids.Input.MailingAddress.Prefix}
              />
            )}

            <View style={styles.errorContainer}>
              <Text style={{ color: 'red' }}>{apolloError}</Text>
            </View>
          </View>
          <Text style={styles.errorText}>{addressValidationError} </Text>
        </View>
      </ScrollView>
      <View style={{ marginBottom: 10 }}>
        <Button
          title="Continue"
          disabled={!isContinueEnabled || isContinueButtonLoading}
          loading={isContinueButtonLoading}
          onPress={onContinuePress}
          testID={E2E.Config.Ids.Press.FindHomeContinue}
        />
      </View>
    </CardApplicationWrapper>
  )
}

const styles = StyleSheet.create({
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'red',
  },
  addressRestrictionText: {
    marginVertical: 16,
    lineHeight: 24,
    color: mesaColorNames['Greyscale/400 #A4AAAC'],
  },
  errorText: {
    color: mesaColorNames['semantic/fg/error #DB340B'],
    fontSize: 16,
    marginBottom: 10,
  },
})
